import { useState } from "react";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Controller, useFormContext } from "react-hook-form";
import ReactCountryFlag from "react-country-flag";
import { getData } from "country-list";
import { DirectorFormData } from "../../types/director";
import PermissionGate from "~/components/PermissionGate";
import CustomeButton from "~/components/Button";

const countries = getData()
  .map((country) => ({
    name: country.name,
    code: country.code,
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

interface IdentificationFormProps {
  isEditMode?: boolean;
  onDiscard?: () => void;
  onSave?: () => void;
  isSaving?: boolean;
  isEditing?: boolean;
  setIsEditing?: (value: boolean) => void;
  onViewHistory?: () => void;
  showViewHistory?: boolean;
  entityId?: string;
}

const IdentificationForm = ({
  isEditMode = false,
  onDiscard,
  onSave,
  isSaving = false,
  isEditing: isEditingProp,
  setIsEditing: setIsEditingProp,
  onViewHistory,
  showViewHistory = false,
  entityId,
}: IdentificationFormProps) => {
  const [isEditingLocal, setIsEditingLocal] = useState(!isEditMode);

  // Use props if provided, otherwise fall back to local state
  const isEditing =
    isEditingProp !== undefined ? isEditingProp : isEditingLocal;
  const setIsEditing = setIsEditingProp || setIsEditingLocal;

  const {
    control,
    reset,
    formState: { errors, isValid, isDirty },
  } = useFormContext<DirectorFormData>();

  const isDisabled = isEditMode && !isEditing;
  const canSave = isDirty && isValid;

  const handleEnableEdit = () => {
    setIsEditing(true);
  };

  const handleDiscard = () => {
    reset(); // Reset form to last saved/default values
    setIsEditing(false);
    onDiscard?.();
  };

  const handleSave = () => {
    onSave?.();
  };

  return (
    <div className="director-form flex flex-column gap-2">
      {/* Header */}
      <div className="flex justify-content-between align-items-start">
        <div className="flex flex-column gap-2">
          <p className="form-title m-0">Identification</p>
          <div className="form-description m-0">
            {isEditMode
              ? "Personal identification details and supporting documents for this director."
              : "Please provide the necessary details and supporting documents for this director"}
          </div>
        </div>
        <PermissionGate
          resource="director"
          action="modify"
          resourceAttr={{ entity_id: entityId }}
        >
          {isEditMode && (
            <div className="flex gap-2">
              {isEditing ? (
                <>
                  <CustomeButton
                    label="Discard"
                    variant="outlined"
                    className="mr-2"
                    onClick={handleDiscard}
                  />
                  <CustomeButton
                    label="Save Changes"
                    variant="contained"
                    onClick={handleSave}
                    loading={isSaving}
                    disabled={!canSave || isSaving}
                    className={!canSave ? "disabled-btn" : ""}
                    iconPos="right"
                  />
                </>
              ) : (
                <>
                  {showViewHistory && (
                    <CustomeButton
                      label="View History"
                      variant="outlined"
                      className="mr-2"
                      icon="pi pi-history"
                      iconPos="right"
                      onClick={onViewHistory}
                    />
                  )}
                  <CustomeButton
                    label="Edit"
                    variant="contained"
                    className="mr-2"
                    icon="pi pi-pencil"
                    iconPos="right"
                    onClick={handleEnableEdit}
                  />
                </>
              )}
            </div>
          )}
        </PermissionGate>
      </div>

      {/* Social Security Section */}
      <div className="flex flex-column gap-2 mt-3">
        <p className="form-section-title m-0">Social Security</p>
      </div>

      <div className="row flex gap-3">
        <div className="flex flex-column col-12 pl-0 pr-0 gap-2">
          <label htmlFor="social_security_number">Social Security Number</label>
          <Controller
            name="social_security_number"
            control={control}
            render={({ field }) => (
              <InputText
                {...field}
                placeholder="Enter Social Security Number"
                type="text"
                disabled={isDisabled}
                className={`input-field appearance-none outline-none w-full ${
                  errors.social_security_number ? "error" : ""
                }`}
              />
            )}
          />
          {errors.social_security_number && (
            <small className="error-message">
              {errors.social_security_number.message}
            </small>
          )}
        </div>
      </div>

      {/* Passport Section */}
      <div className="flex flex-column gap-2 mt-3">
        <p className="form-section-title m-0">Passport</p>
      </div>

      <div className="row flex gap-3">
        <div className="flex flex-column col-12 pl-0 pr-0 gap-2">
          <label htmlFor="passport_number">Passport Number</label>
          <Controller
            name="passport_number"
            control={control}
            render={({ field }) => (
              <InputText
                {...field}
                placeholder="Enter Passport Number"
                type="text"
                disabled={isDisabled}
                className={`input-field appearance-none outline-none w-full ${
                  errors.passport_number ? "error" : ""
                }`}
              />
            )}
          />
          {errors.passport_number && (
            <small className="error-message">
              {errors.passport_number.message}
            </small>
          )}
        </div>
      </div>

      <div className="row flex gap-3">
        <div className="flex flex-column col-6 pl-0 pr-0 gap-2">
          <label htmlFor="country_of_issue">Country of Issue</label>
          <div style={{ position: "relative" }}>
            <Controller
              name="country_of_issue"
              control={control}
              render={({ field }) => (
                <Dropdown
                  {...field}
                  options={countries}
                  optionLabel="name"
                  placeholder="Select Country of Issue"
                  disabled={isDisabled}
                  className={`officer-dropdown-field w-full ${
                    errors.country_of_issue ? "error" : ""
                  }`}
                  appendTo="self"
                  itemTemplate={(option) => (
                    <div className="flex align-items-center">
                      <ReactCountryFlag
                        countryCode={option.code}
                        svg
                        className="flag-svg-icon"
                      />
                      <span>{option.name}</span>
                    </div>
                  )}
                  valueTemplate={(option) => {
                    return option ? (
                      <div className="flex align-items-center">
                        <ReactCountryFlag
                          countryCode={option.code}
                          svg
                          className="flag-svg-icon"
                        />
                        <span>{option.name}</span>
                      </div>
                    ) : (
                      "Select Country of Issue"
                    );
                  }}
                />
              )}
            />
          </div>
          {errors.country_of_issue && (
            <small className="error-message">
              {errors.country_of_issue.message}
            </small>
          )}
        </div>
        <div className="flex flex-column col-6 pl-0 pr-0 gap-2">
          <label htmlFor="date_of_birth">Date of Birth</label>
          <Controller
            name="date_of_birth"
            control={control}
            render={({ field }) => (
              <Calendar
                {...field}
                value={field.value ? new Date(field.value) : null}
                disabled={isDisabled}
                onChange={(e) => field.onChange(e.value)}
                placeholder="Enter Date of Birth"
                className={`w-full calendar-field ${errors.date_of_birth ? "error" : ""}`}
                dateFormat="dd/mm/yy"
              />
            )}
          />
          {errors.date_of_birth && (
            <small className="error-message">
              {errors.date_of_birth.message}
            </small>
          )}
        </div>
      </div>

      <div className="row flex gap-3">
        <div className="flex flex-column col-6 pl-0 pr-0 gap-2">
          <label htmlFor="nationality">Nationality</label>
          <div style={{ position: "relative" }}>
            <Controller
              name="nationality"
              control={control}
              render={({ field }) => (
                <Dropdown
                  {...field}
                  options={countries}
                  optionLabel="name"
                  placeholder="Enter Nationality"
                  disabled={isDisabled}
                  appendTo="self"
                  className={`officer-dropdown-field w-full ${
                    errors.nationality ? "error" : ""
                  }`}
                  itemTemplate={(option) => (
                    <div className="flex align-items-center">
                      <ReactCountryFlag
                        countryCode={option.code}
                        svg
                        className="flag-svg-icon"
                      />
                      <span>{option.name}</span>
                    </div>
                  )}
                  valueTemplate={(option) => {
                    return option ? (
                      <div className="flex align-items-center">
                        <ReactCountryFlag
                          countryCode={option.code}
                          svg
                          className="flag-svg-icon"
                        />
                        <span>{option.name}</span>
                      </div>
                    ) : (
                      "Enter Nationality"
                    );
                  }}
                />
              )}
            />
          </div>
          {errors.nationality && (
            <small className="error-message">
              {errors.nationality.message}
            </small>
          )}
        </div>
        <div className="flex flex-column col-6 pl-0 pr-0 gap-2">
          <label htmlFor="expiry_date">Expiry Date</label>
          <Controller
            name="expiry_date"
            control={control}
            render={({ field }) => (
              <Calendar
                {...field}
                value={field.value ? new Date(field.value) : null}
                onChange={(e) => field.onChange(e.value)}
                placeholder="Enter Expiry Date"
                disabled={isDisabled}
                className={`w-full calendar-field ${errors.expiry_date ? "error" : ""}`}
                dateFormat="dd/mm/yy"
              />
            )}
          />
          {errors.expiry_date && (
            <small className="error-message">
              {errors.expiry_date.message}
            </small>
          )}
        </div>
      </div>
    </div>
  );
};

export default IdentificationForm;
